import { useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { Lock, Loader2 } from "lucide-react";
import { Input } from "@/components/ui/input";
import { useAuth, formatApiErrorDetail } from "@/components/AuthContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function AdminLogin() {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const { setUser } = useAuth();
  const navigate = useNavigate();

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      const { data } = await axios.post(`${API}/auth/login`, { email, password }, { withCredentials: true });
      setUser(data);
      navigate("/admin/reservations");
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="mx-auto flex max-w-md flex-col items-center px-6 py-24" data-testid="admin-login-page">
      <div className="w-full rounded-2xl border border-[#2C2A29]/10 bg-white p-10 shadow-sm">
        <div className="flex justify-center">
          <img src="/assets/logo-argia.png" alt="ARGIA Admin" className="h-16 w-auto" />
        </div>
        <h1 className="mt-6 text-center font-display text-2xl font-bold text-argia-ink">Espace pro</h1>
        <p className="mt-2 text-center text-sm text-argia-muted">Réservé à Aurélie — gestion des réservations</p>
        <form onSubmit={submit} className="mt-8 space-y-5">
          <div>
            <label className="mb-1.5 block text-sm font-semibold text-argia-ink">Email</label>
            <Input data-testid="admin-login-email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} required autoComplete="email" />
          </div>
          <div>
            <label className="mb-1.5 block text-sm font-semibold text-argia-ink">Mot de passe</label>
            <Input data-testid="admin-login-password" type="password" value={password} onChange={(e) => setPassword(e.target.value)} required autoComplete="current-password" />
          </div>
          {error && (
            <p className="rounded-lg bg-red-50 px-4 py-3 text-sm text-argia-red" data-testid="admin-login-error">{error}</p>
          )}
          <button
            type="submit"
            data-testid="admin-login-submit"
            disabled={loading}
            className="inline-flex w-full items-center justify-center gap-2 rounded-full bg-argia-red px-6 py-3 text-sm font-semibold text-white transition-colors duration-300 hover:bg-argia-redDark disabled:opacity-40"
          >
            {loading ? <Loader2 size={16} className="animate-spin" /> : <Lock size={16} strokeWidth={1.5} />}
            Se connecter
          </button>
        </form>

        <div className="mt-8 flex items-center gap-4">
          <span className="h-px flex-1 bg-[#2C2A29]/10" />
          <span className="text-xs uppercase tracking-widest text-argia-muted">ou</span>
          <span className="h-px flex-1 bg-[#2C2A29]/10" />
        </div>

        <button
          type="button"
          data-testid="admin-login-google"
          onClick={() => {
            // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
            const redirectUrl = window.location.origin + "/admin/reservations";
            window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
          }}
          className="mt-6 inline-flex w-full items-center justify-center gap-3 rounded-full border border-[#2C2A29]/15 bg-white px-6 py-3 text-sm font-semibold text-argia-ink transition-colors duration-300 hover:bg-[#2C2A29]/[0.04]"
        >
          <svg width="18" height="18" viewBox="0 0 48 48" aria-hidden="true">
            <path fill="#EA4335" d="M24 9.5c3.5 0 6.6 1.2 9 3.6l6.7-6.7C35.6 2.6 30.1.5 24 .5 14.6.5 6.5 5.8 2.6 13.6l7.8 6.1C12.3 13.7 17.6 9.5 24 9.5z" />
            <path fill="#4285F4" d="M46.6 24.5c0-1.6-.1-2.8-.4-4.1H24v8.5h12.8c-.6 3.1-2.5 5.4-5.2 7l7.6 5.9c4.4-4.1 7.4-10.1 7.4-17.3z" />
            <path fill="#FBBC05" d="M10.4 28.3c-.5-1.4-.8-2.9-.8-4.3s.3-3 .8-4.3l-7.8-6.1C1 16.6 0 20.2 0 24s1 7.4 2.6 10.4l7.8-6.1z" />
            <path fill="#34A853" d="M24 47.5c6.1 0 11.3-2 15.2-5.6l-7.6-5.9c-2.1 1.4-4.8 2.3-7.6 2.3-6.4 0-11.7-4.2-13.6-10.1l-7.8 6.1C6.5 42.2 14.6 47.5 24 47.5z" />
          </svg>
          Se connecter avec Google
        </button>
        <p className="mt-4 text-center text-xs text-argia-muted">
          Seul le compte Google d'Aurélie est autorisé.
        </p>
      </div>
    </div>
  );
}
